<script lang="ts">
  import type { Match } from '../lib/match.svelte';
  import { figures, oversFrom } from '../lib/commentary';

  interface Props {
    match: Match;
    onscorecard: () => void;
    onagain: () => void;
    onhome: () => void;
  }
  let { match, onscorecard, onagain, onhome }: Props = $props();

  const r = $derived(match.result);

  /**
   * The two performances worth naming: the top score and the best figures.
   * Best figures is wickets first, then runs conceded — 3 for 24 beats 2 for 12,
   * which a runs-only sort would get backwards.
   */
  const best = $derived.by(() => {
    if (!r) return { bat: null, bowl: null };
    const batters = [...r.first.batting, ...r.second.batting].filter((b) => b.balls > 0);
    const bowlers = [...r.first.bowling, ...r.second.bowling].filter((b) => b.balls > 0);
    return {
      bat: batters.sort((a, b) => b.runs - a.runs)[0] ?? null,
      bowl: bowlers.sort((a, b) => b.wickets - a.wickets || a.runs - b.runs)[0] ?? null,
    };
  });
</script>

<section class="screen active" aria-label="Result">
  <div class="body">
    {#if r}
      <div class="verdict">
        <div class="big">{r.headline}</div>
        {#if r.detail}<p class="muted">{r.detail}</p>{/if}
      </div>

      <div class="card">
        <div class="vs">
          <span>
            <span class:you-team={r.firstName === match.youName} class:opp-team={r.firstName !== match.youName}>
              {r.firstName}
            </span><br />
            <span class="s tabular">{r.first.runs}/{r.first.wickets}</span>
          </span>
          <span class="faint">{r.tied ? 'tied with' : r.chaseWon ? 'lost to' : 'beat'}</span>
          <span>
            <span class:you-team={r.secondName === match.youName} class:opp-team={r.secondName !== match.youName}>
              {r.secondName}
            </span><br />
            <span class="s tabular">
              {r.second.runs}/{r.second.wickets} ({oversFrom(r.second.legalBalls)})
            </span>
          </span>
        </div>
      </div>

      <p class="eyebrow" style="margin-top:var(--space-5)">Standout performers</p>
      {#if best.bat}
        <div class="card">
          <strong>{best.bat.player.name}</strong>
          <span class="muted tabular">{best.bat.runs} ({best.bat.balls})</span>
        </div>
      {/if}
      {#if best.bowl}
        <div class="card">
          <strong>{best.bowl.player.name}</strong>
          <span class="muted tabular">
            {figures(best.bowl.balls, best.bowl.runs, best.bowl.wickets, best.bowl.maidens)}
          </span>
        </div>
      {/if}
    {/if}
  </div>

  <div class="actions">
    <button class="btn-primary" onclick={onscorecard}>Full scorecard</button>
    <div class="row2">
      <button class="btn-secondary" onclick={onagain}>Draft again</button>
      <button class="btn-ghost" onclick={onhome}>Home</button>
    </div>
  </div>
</section>
